import { useEffect, useRef } from "react";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { CometChatUIKitLoginListener } from "@cometchat/chat-uikit-react";
import { Selection } from "./useChatData";
import { messageBelongsToSelection } from "./messageMatch";
import * as sounds from "./sounds";

/**
 * Browser/desktop notifications for new messages and incoming calls.
 * Fires when the tab is hidden/unfocused, or when the message belongs to
 * a conversation other than the one currently open. Clicking a
 * notification focuses the app and jumps to that conversation. Also keeps
 * an unread badge in the tab title ("(3) dbm. chat") until refocused.
 *
 * All Notification API access is guarded — inside the Softr iframe the
 * permission may be unavailable or denied, and everything degrades
 * silently (the pop-out window gets the full experience).
 */

const LISTENER_ID = "dbm-notify";
const CALL_LISTENER_ID = "dbm-notify-call";
const BASE_TITLE = "dbm. chat";

function canNotify(): boolean {
  return typeof window !== "undefined" && "Notification" in window;
}

type Options = {
  currentSelection: Selection | null;
  onOpen: (s: Selection) => void;
};

export function useNotifications({ currentSelection, onOpen }: Options) {
  const selectionRef = useRef<Selection | null>(currentSelection);
  selectionRef.current = currentSelection;
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;
  const unreadRef = useRef(0);

  useEffect(() => {
    if (!canNotify()) return;
    try {
      if (Notification.permission === "default") {
        void Notification.requestPermission();
      }
    } catch {
      /* iframe or browser policy — degrade silently */
    }
  }, []);

  useEffect(() => {
    const bumpTitle = () => {
      document.title = unreadRef.current > 0 ? `(${unreadRef.current}) ${BASE_TITLE}` : BASE_TITLE;
    };
    const onFocus = () => {
      unreadRef.current = 0;
      bumpTitle();
    };
    window.addEventListener("focus", onFocus);

    const show = (title: string, body: string, tag: string, selection: Selection | null) => {
      unreadRef.current += 1;
      bumpTitle();
      if (!canNotify() || Notification.permission !== "granted") return;
      try {
        const n = new Notification(title, { body, tag, icon: "/favicon.svg" });
        n.onclick = () => {
          try {
            window.focus();
          } catch {
            /* ignore */
          }
          if (selection) onOpenRef.current(selection);
          n.close();
        };
      } catch {
        /* constructor can throw on some platforms (e.g. Android Chrome) */
      }
    };

    const messageSelection = (msg: CometChat.BaseMessage): Selection | null => {
      if (msg.getReceiverType() === "group") {
        const receiver = msg.getReceiver();
        const name =
          receiver && "getName" in receiver ? (receiver as CometChat.Group).getName() : msg.getReceiverId();
        return { kind: "group", id: msg.getReceiverId(), name, subtitle: "Channel" };
      }
      const sender = msg.getSender();
      if (!sender) return null;
      return { kind: "user", id: sender.getUid(), name: sender.getName(), subtitle: "Direct message" };
    };

    const isConversationOpen = (msg: CometChat.BaseMessage): boolean =>
      messageBelongsToSelection(msg, selectionRef.current);

    const preview = (msg: CometChat.BaseMessage): string => {
      if (msg.getType() === "text") {
        const text = (msg as CometChat.TextMessage).getText() || "";
        return text.length > 120 ? text.slice(0, 117) + "…" : text;
      }
      if (msg.getType() === CometChat.MESSAGE_TYPE.IMAGE) return "Sent a photo";
      if (msg.getType() === CometChat.MESSAGE_TYPE.VIDEO) return "Sent a video";
      if (msg.getType() === CometChat.MESSAGE_TYPE.AUDIO) return "Sent an audio clip";
      if (msg.getType() === CometChat.MESSAGE_TYPE.FILE) return "Sent a file";
      return "New message";
    };

    const onMessage = (msg: CometChat.BaseMessage) => {
      const meUid = CometChatUIKitLoginListener.getLoggedInUser()?.getUid();
      const sender = msg.getSender();
      if (!sender || sender.getUid() === meUid) return;

      // Sound plays for any conversation other than the one you're
      // actively looking at, independent of tab focus — a message on the
      // open chat while you're briefly alt-tabbed away doesn't need a
      // ding, but every other conversation does.
      if (!isConversationOpen(msg)) sounds.playOnce(sounds.SOUND.messageReceived);

      const visibleHere = document.hasFocus() && isConversationOpen(msg);
      if (visibleHere) return;

      const selection = messageSelection(msg);
      const isGroup = msg.getReceiverType() === "group";
      const title = isGroup ? `${sender.getName()} · #${selection?.name ?? "channel"}` : sender.getName();
      show(title, preview(msg), `msg-${msg.getConversationId?.() ?? msg.getReceiverId()}`, selection);
    };

    CometChat.addMessageListener(
      LISTENER_ID,
      new CometChat.MessageListener({
        onTextMessageReceived: onMessage,
        onMediaMessageReceived: onMessage,
      })
    );

    CometChat.addCallListener(
      CALL_LISTENER_ID,
      new CometChat.CallListener({
        onIncomingCallReceived: (call: CometChat.Call) => {
          if (document.hasFocus()) return; // the in-app card + ring handles it
          const name = call.getCallInitiator()?.getName() ?? "Someone";
          const kind = call.getType() === CometChat.CALL_TYPE.AUDIO ? "audio" : "video";
          show(`Incoming ${kind} call`, name, `call-${call.getSessionId()}`, null);
        },
      })
    );

    return () => {
      window.removeEventListener("focus", onFocus);
      CometChat.removeMessageListener(LISTENER_ID);
      CometChat.removeCallListener(CALL_LISTENER_ID);
      document.title = BASE_TITLE;
    };
  }, []);
}
